import { call, post } from "@/services/api";

import type { MediaFile } from "@/services/media";

export type ReceiptLine = {
  id: number; sku: string | null; label: string | null; myotaku_product_id: number | null;
  ordered_qty: number | null; received_qty: number | null;
  box_references: string[]; state: "complete" | "missing" | "extra" | "matched" | "unmatched"; shortfall: number;
};
export type DocumentType = "delivery_note" | "order_confirmation" | "invoice" | "labels" | "other";
export type Receipt = {
  id: number; myotaku_order_id: number | null; myotaku_warehouse_id: number | null;
  supplier_name: string | null; document_reference: string | null;
  document_date: string | null; received_on: string | null;
  source_kind: string | null; status: "draft" | "reading" | "read" | "failed" | "checked" | "stopped" | "verified"; read_at: string | null; note: string | null;
  document_type: DocumentType | null; carrier: string | null; pages_count: number | null;
  read_error: string | null; reading_started_at: string | null; created_by: string | null; proves_arrival: boolean;
  // Why a reading failed, worded by the screen (his `recErr`); the stored reason is never shown.
  read_error_kind: "rate_limited" | "unavailable" | "photo" | "failed" | null;
  // His card names the warehouse the document was received at.
  warehouse_name: string | null;
  summary: { total: number; complete: number; missing: number; extra: number; unmatched: number; matched: number; clients: number;
    // His list of latest documents: the clients a document reached, by name.
    client_names?: string[] };
  lines?: ReceiptLine[];
  media?: MediaFile[];
  result?: ReceptionResult;
  purpose?: "furniture" | "articles"; declared_type?: string | null;
  // His "Stock mis à jour : … +n (total N)" and "À vérifier : …" for an article receipt.
  stocked?: null | {
    // supplier_ref: the supplier's own code read on the note, printed on the carton label.
    items: { reference: string; qty: number; total: number | null; supplier_ref?: string | null }[];
    to_verify: { no: string | null; desc: string | null; qty: number | null; why: string }[];
    // His task A20 (answers of 03.11.2026): each client's part, never stocked; the reading's one
    // sentence; whether the reception is "à vérifier" (a line to check, or nothing read).
    client_reserved?: { reference: string | null; desc: string | null; qty: number | null; client: string }[];
    summary?: string | null;
    to_check?: boolean | null;
    // His « Vérifiée et corrigée » (answers of 03.11.2026, line 15): who, when, the note, the corrections.
    verified?: null | { at: string | null; by: string | null; note: string | null;
      corrections: { code: string; name: string | null; delta: number; new_qty: number }[] };
    cal_check?: CalCheck | null;
  };
};

// His task A28's result on one reception (`calCheck`, scripts/cal_check.py, 03.11.2026): it matches
// its Delivery calendar order(s), it differs (items: ordered vs photographed; ordered null = not in the
// order), the order is not marked delivered yet, or no Penthalaz order has these articles.
export type CalCheck = {
  status: "ok" | "ecart" | "non_livree" | "absent_calendar";
  order_ids: number[];
  items?: { code: string; name: string | null; ordered: number | null; photo: number }[];
  order_statuses?: string[];
  since?: string;
  at?: string | null;
};
// His `automation/calendarCheck`: the last run, and the Penthalaz orders arrived with no photo yet.
export type CalendarCheckState = {
  warehouse_id: number; last_run_at: string | null; last_status: string | null;
  // false while its Slack messages are held (config/app_config.yml calendar_check.messages).
  messages?: boolean;
  // false while his supplierCatalog is not imported: an order known only there is not checked.
  catalogue_imported?: boolean;
  missing: { order_id: number; arrival: string | null; supplier: string | null; items: { code: string; qty: number }[] }[];
};

// What a read document means for each client (his recRows): per delivery, each furniture with what was
// ordered and what arrived. "not_on_note" is his "pas sur ce bon" — never "missing".
export type ReceptionRow = {
  name: string | null; sku: string | null; product_id: number | null; image_url: string | null;
  ordered: number; received: number; status: "complete" | "short" | "extra" | "not_on_note";
  cartons: number; parts: { no: string; desc: string | null; qty: number }[]; colli: number | null;
  cartons_incomplete: boolean;
  // The supplier order the piece is on (his "· order #n" on each row), and the furniture type his row
  // subtitle names (his `lbl`).
  order_id?: number | null;
  product_type_slug?: string | null;
};
export type ReceptionBlock = {
  // day: the assembly job's own day (his "montage <day>"); delivery_on: the delivery its title names.
  event: { id: number; number: number | null; client_name: string | null; contract_id: number | null;
           day: string | null; delivery_on: string | null;
           starts_at: string | null; type_label: string | null; warehouse: { id: number; name: string } | null };
  rows: ReceptionRow[];
};
export type NoteLine = { id: number; no: string | null; desc: string | null; qty: number | null; ref: string | null; page: number | null };
export type ReceptionResult = {
  proves_arrival: boolean;
  blocks: ReceptionBlock[];
  unmatched: {
    none?: NoteLine[];
    many?: (NoteLine & { clients: { event_id: number; client_name: string | null; contract_name: string | null; starts_at: string | null }[] })[];
    unread?: NoteLine[];
  };
  unclear: string[];
};
export type Transfer = {
  id: number; kit_code: string | null; article_reference: string | null;
  from_myotaku_warehouse_id: number; to_myotaku_warehouse_id: number;
  qty: number; status: string; sent_at: string | null; received_at: string | null;
  sent_by: string | null; received_by: string | null; note: string | null;
  received_qty: number | null; receive_note: string | null;
};


export const fetchReceipts = (w?: number | null, purpose: "furniture" | "articles" = "furniture") =>
  call<{ receipts: Receipt[]; calendar_check?: CalendarCheckState | null }>(`/api/v1/receiving?purpose=${purpose}${w ? `&warehouse_id=${w}` : ""}`);
export const fetchReceipt = (id: number) => call<Receipt>(`/api/v1/receiving/${id}`);
export const createReceipt = (b: Record<string, unknown>) => post<Receipt>(`/api/v1/receiving`, b);
export const reconcileReceipt = (id: number) => post<Receipt>(`/api/v1/receiving/${id}/reconcile`);
export const readReceipt = (id: number) => post<Receipt>(`/api/v1/receiving/${id}/read`);
// His "Arrêter": the reading stops before its next page and keeps nothing; the pages stay.
export const stopReading = (id: number) => post<Receipt>(`/api/v1/receiving/${id}/stop`);
export const rematchReceipt = (id: number) => post<Receipt>(`/api/v1/receiving/${id}/rematch`);
// His « ✓ Vérifié et corrigé »: the stock corrections (article + quantity, plus or minus), then a note.
export const verifyReceipt = (id: number, note: string, corrections: { code: string; delta: number }[]) =>
  post<Receipt>(`/api/v1/receiving/${id}/verify`, { note, corrections });
// His « Références fournisseur contestées » and « Valider ».
export type DisputedReference = {
  id: number; supplier_reference: string; supplier: string | null; supplier_name: string | null;
  article_reference: string | null; article_name: string | null; note: string | null; unit_note: string | null;
};
export const fetchDisputedReferences = () => call<{ references: DisputedReference[] }>(`/api/v1/supplier_references/disputed`);
export const validateReference = (id: number, articleReference: string) =>
  post<{ reference: DisputedReference }>(`/api/v1/supplier_references/${id}/validate`, { article_reference: articleReference });
export const fetchReceptionForEvent = (eventId: number) =>
  call<{ receipts: (Receipt & { result: ReceptionResult })[] }>(`/api/v1/receiving/for_event/${eventId}`);

// His "sans références": composite furniture in several cartons whose carton references are unknown.
export type CartonsUnknown = { run: { ran_at: string; finished_at: string | null; error: string | null;
  outcome: { without_references: { sku: string; title: string | null; cartons: number; order_ids: number[] }[]; documents_read: number } | null } | null };
export const fetchCartonsUnknown = () => call<CartonsUnknown>(`/api/v1/receiving/cartons_unknown`);
export const fetchTransfers = (w?: number | null) =>
  call<{ transfers: Transfer[] }>(`/api/v1/transfers${w ? `?warehouse_id=${w}` : ""}`);
export const createTransfer = (b: Record<string, unknown>) => post<Transfer>(`/api/v1/transfers`, b);
export const sendTransfer = (id: number) => post<Transfer>(`/api/v1/transfers/${id}/send`);
// Ludovic's answer E5.2: the destination says how many arrived, and why when fewer than were sent.
export const receiveTransfer = ({ id, receivedQty, note }: { id: number; receivedQty: number; note: string }) =>
  post<Transfer>(`/api/v1/transfers/${id}/receive`, { received_qty: receivedQty, note });
